import { buildApiUrl, ADMIN_ENDPOINTS } from '../../configs/api.js'

export default {
  url(payload) {
    const { userId, startDate, endDate } = payload || {}

    // Fetch a large batch — filtering by type/status is done locally
    // so the table can switch filters without re-fetching
    return buildApiUrl(ADMIN_ENDPOINTS.ADMIN_TRANSACTIONS, {
      queryParams: {
        limit: 2000,
        userId: userId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }
    })
  },

  parse(raw = {}, payload = {}) {
    if (raw.statusCode !== 200) {
      return {
        statusCode: raw?.statusCode || 400,
        message: raw?.message || 'Failed to fetch transactions',
        data: null,
      }
    }

    const transactions = Array.isArray(raw.data) ? raw.data : (raw.data?.data || [])
    const total = raw.total ?? transactions.length

    const formattedTransactions = transactions.map(tx => {
      // Description intelligente
      let description = tx.description || tx.metadata?.narration || ''
      if (!description) {
        if (tx.src?.provider) {
          description = `Rechargement via ${tx.src.provider}${tx.src.processor ? ` (${tx.src.processor})` : ''}`
        } else if (tx.metadata?.reason) {
          description = tx.metadata.reason
        } else {
          description = 'Transaction système'
        }
      }

      // Type crédit/débit — déterminé depuis l'id de la transaction
      let type = tx.type
      if (!type) {
        if (tx.id?.includes('credit')) type = 'credit'
        else if (tx.id?.includes('debit')) type = 'debit'
        else type = 'credit'
      }

      // Normalisation du statut — l'API renvoie dstStatus: "success" | "failed" | "unstarted" | ...
      // Tout ce qui n'est ni success ni failed → en attente
      const rawStatus =  tx.status || ''
      let status
      if (rawStatus === 'success') status = 'completed'
      else if (rawStatus === 'failed') status = 'failed'
      else status = 'pending'

      // Infos payeur
      const firstName = tx.firstName || tx.src?.firstName || ''
      const lastName = tx.lastName || tx.src?.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim() || 'Inconnu'

      // Téléphone : pour les retraits (debit) c'est dans tx.dst.phoneNumber
      const phone = tx.src?.phoneNumber || tx.dst?.phoneNumber || tx.phoneNumber || ''

      return {
        id: tx.id,
        payerName: fullName,
        payerEmail: tx.email || '',
        payerPhone: phone,
        amount: tx.amount || tx.dst?.amount || tx.src?.amount || 0,
        currency: tx.currency || tx.dst?.currency || 'XOF',
        type,     // 'credit' (dépôt) | 'debit' (retrait)
        status,   // 'completed' | 'failed' | 'pending'
        paymentMethod: tx.dst?.provider || tx.src?.provider || tx.dst?.processor || 'wallet',
        reference: tx.paymentReference || tx.id,
        date: tx.createdAt,
        description,
        source: tx.source || (tx.dst?.processor === 'rapidoWallet' ? 'wallet' : 'system'),
      }
    })


    let finalData = formattedTransactions

    // === Recherche textuelle locale (nom, ref, description) ===
    const searchTerm = (payload?.search || '').toString().trim().toLowerCase()
    if (searchTerm) {
      finalData = finalData.filter(el => {
        return (
          (el.payerName && el.payerName.toLowerCase().includes(searchTerm)) ||
          (el.payerEmail && el.payerEmail.toLowerCase().includes(searchTerm)) ||
          (el.payerPhone && el.payerPhone.toLowerCase().includes(searchTerm)) ||
          (el.reference && el.reference.toLowerCase().includes(searchTerm)) ||
          (el.description && el.description.toLowerCase().includes(searchTerm)) ||
          (el.id && el.id.toLowerCase().includes(searchTerm))
        )
      })
    }

    // === Filtres col (type / status) passés par TableList via colFilters ===
    const typeFilter = payload?.type
    if (typeFilter) {
      finalData = finalData.filter(el => el.type === typeFilter)
    }

    const statusFilter = payload?.status
    if (statusFilter) {
      finalData = finalData.filter(el => el.status === statusFilter)
    }

    // === Tri dynamique (sortKey / sortOrder depuis TableList) ===
    const { sortKey, sortOrder } = payload || {}
    if (sortKey && sortKey !== 'none') {
      const order = sortOrder === 'desc' ? -1 : 1
      finalData = [...finalData].sort((a, b) => {
        const valA = a?.[sortKey]
        const valB = b?.[sortKey]
        if (typeof valA === 'string' && typeof valB === 'string') return valA.localeCompare(valB) * order
        if (typeof valA === 'number' && typeof valB === 'number') return (valA - valB) * order
        return 0
      })
    } else {
      // Par défaut : plus récent en premier
      finalData = [...finalData].sort((a, b) => new Date(b.date) - new Date(a.date))
    }

    // === Pagination locale ===
    const { page = 0, limit = 20 } = payload || {}
    const paginated = finalData.slice(page * limit, (page + 1) * limit)

    return {
      statusCode: 200,
      message: 'Ok',
      data: {
        total: (searchTerm || typeFilter || statusFilter) ? finalData.length : total,
        result: paginated,
      },
    }
  },
}
